import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Correcting the client. A typo here used to be permanent, because the name
 *  lived inside every trip separately; now it is one record and one edit. */
export default function EditClientDialog({ open, onClose, client, t, onSaved }) {
  const [name, setName] = useState(client.name || "");
  const [city, setCity] = useState(client.city || "");
  const [contact, setContact] = useState(client.contact || "");
  const [notes, setNotes] = useState(client.notes || "");
  const [saving, setSaving] = useState(false);

  // Seed from the client each time it opens, so an abandoned edit leaves
  // nothing behind for the next one.
  useEffect(() => {
    if (!open) return;
    setName(client.name || "");
    setCity(client.city || "");
    setContact(client.contact || "");
    setNotes(client.notes || "");
  }, [open, client]);

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/trasferte/clients/${client.id}`,
        {
          name: name.trim(),
          city: city.trim(),
          contact: contact.trim(),
          notes: notes.trim(),
        },
        { withCredentials: true }
      );
      onSaved(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("trasferte.clientSaveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{t("trasferte.editClient")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3 mt-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("trasferte.clientName")}
            data-testid="client-edit-name"
            required
            className="border-slate-200"
          />
          <Input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder={t("trasferte.city")}
            data-testid="client-edit-city"
            className="border-slate-200"
          />
          <Input
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            placeholder={t("trasferte.clientContactPlaceholder")}
            data-testid="client-edit-contact"
            className="border-slate-200"
          />
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder={t("trasferte.clientNotesPlaceholder")}
            data-testid="client-edit-notes"
            className="border-slate-200 resize-none"
          />
          <div className="flex flex-wrap justify-end gap-3 pt-1">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving} className="border-slate-200">
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={saving || !name.trim()} data-testid="client-edit-save" className="bg-indigo-700 hover:bg-indigo-600 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("common.save")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
